import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { Attachment, AttachmentSubject, ImageSource } from '@kronos/domain'
import { repository } from '../data'
import { imageUrl, prepareImage } from './imageFile'

/**
 * Photographs of a thing: showing them, and adding one.
 *
 * §6 makes this load-bearing rather than decorative — a component removed for
 * failure needs a teardown *with photos* before it can be scrapped, and §1's
 * argument is that a lesson from these six robots is worth roughly two hundred
 * later ones. "The seal looked like this" is a sentence a photograph makes and
 * prose does not.
 *
 * The upload path is deliberately one tap on a phone: `capture="environment"`
 * opens the rear camera directly rather than a file browser, because the person
 * is standing in front of the thing.
 */

export function PhotoStrip({
  subject,
  canEdit,
  canDelete = false,
}: {
  subject: AttachmentSubject
  canEdit: boolean
  canDelete?: boolean
}): JSX.Element {
  const client = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [pending, setPending] = useState<string[]>([])

  const key = ['attachments', subject.kind, subject.id]
  const photos = useQuery({
    queryKey: key,
    queryFn: () => repository.attachments(subject.kind, subject.id),
  })

  const upload = useMutation({
    mutationFn: async (files: readonly File[]) => {
      for (const file of files) {
        const prepared = await prepareImage(file)
        if (!prepared.ok) throw new Error(prepared.reason)
        // Optimistic: the photo appears the instant it is taken, from the
        // local blob, rather than after a round trip over workshop wifi.
        setPending((p) => [...p, prepared.image.previewUrl])
        await repository.uploadAttachment(subject, prepared.image.blob, {
          width: prepared.image.width,
          height: prepared.image.height,
        })
      }
    },
    onSettled: async () => {
      setPending((p) => {
        for (const url of p) URL.revokeObjectURL(url)
        return []
      })
      await client.invalidateQueries({ queryKey: key })
      // A new photo can change what a card shows, several screens away.
      await client.invalidateQueries({ queryKey: ['component'] })
      await client.invalidateQueries({ queryKey: ['fleet'] })
      await client.invalidateQueries({ queryKey: ['stock'] })
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const remove = useMutation({
    mutationFn: (id: string) => repository.deleteAttachment(id),
    onSuccess: async () => {
      await client.invalidateQueries()
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const promote = useMutation({
    mutationFn: (id: string) => repository.makePrimaryAttachment(id),
    onSuccess: async () => {
      await client.invalidateQueries()
    },
  })

  const list = photos.data ?? []
  if (!canEdit && list.length === 0) return <></>

  return (
    <section className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="section-title">Photos</h2>
        {canEdit ? (
          <>
            <input
              ref={input}
              type="file"
              accept="image/*"
              // The rear camera, straight away. On a desktop this is ignored
              // and a file picker opens, which is the right answer there.
              capture="environment"
              multiple
              hidden
              onChange={(e) => {
                setProblem(null)
                const files = [...(e.target.files ?? [])]
                e.target.value = ''
                if (files.length > 0) upload.mutate(files)
              }}
            />
            <button onClick={() => input.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? 'Adding…' : '+ Add a photo'}
            </button>
          </>
        ) : null}
      </div>

      {problem ? <p className="critical">{problem}</p> : null}

      {list.length === 0 && pending.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          None yet. A photograph of this is worth more than a description of it — especially on a
          part that has failed.
        </p>
      ) : (
        <div className="photo-strip">
          {list.map((photo, i) => (
            <figure key={photo.id} className="photo">
              <img src={imageUrl(photo.id)} alt={photo.caption || 'photograph'} loading="lazy" />
              {photo.caption ? <figcaption>{photo.caption}</figcaption> : null}
              {canEdit ? (
                <div className="photo__actions">
                  {i > 0 ? (
                    <button title="Show this one on the card" onClick={() => promote.mutate(photo.id)}>
                      ★
                    </button>
                  ) : (
                    <span className="photo__badge" title="Shown on the card">
                      ★
                    </span>
                  )}
                  {canDelete ? (
                    <button title="Delete" onClick={() => remove.mutate(photo.id)}>
                      ✕
                    </button>
                  ) : null}
                </div>
              ) : null}
            </figure>
          ))}
          {pending.map((url) => (
            <figure key={url} className="photo photo--pending">
              <img src={url} alt="uploading" />
            </figure>
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * The picture at the top of a page, and what it is a picture *of*.
 *
 * When the photo came from the type rather than from this part, the caption
 * says so. The distinction is small and it matters: "here is this actuator" and
 * "here is what an actuator looks like" are different claims, and a page that
 * blurs them is one that will eventually be used to identify the wrong object.
 */
export function HeroImage({
  image,
  alt,
}: {
  image: { readonly id: string; readonly from: ImageSource; readonly caption: string } | null
  alt: string
}): JSX.Element | null {
  const [open, setOpen] = useState(false)
  if (!image) return null

  const provenance =
    image.from === 'component'
      ? null
      : image.from === 'revision'
        ? 'a photo of this revision'
        : 'a photo of this type, not of this one'

  return (
    <>
      <button className="hero" onClick={() => setOpen(true)} title="Show larger">
        <img src={imageUrl(image.id)} alt={alt} />
        {provenance ? <span className="hero__provenance">{provenance}</span> : null}
      </button>
      {open ? (
        <div
          className="lightbox"
          role="dialog"
          aria-label={alt}
          onClick={() => setOpen(false)}
          // Escape closes it, because a phone user's other hand is on a part.
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
          tabIndex={-1}
        >
          <img src={imageUrl(image.id)} alt={alt} />
        </div>
      ) : null}
    </>
  )
}

/** A thumbnail for a list row, or a category-lettered placeholder when a type
 *  has no photo yet — a grey box tells you nothing, an initial tells you a
 *  little and keeps the row heights honest. */
export function Thumb({
  imageId,
  label,
  size = 44,
}: {
  imageId: string | null
  label: string
  size?: number
}): JSX.Element {
  /*
   * A photo that fails to load falls back to the same initial tile as one that
   * was never taken.
   *
   * Without this the browser draws its own broken-image glyph — a grey torn
   * page on a black square — and a Catalog whose blobs have been wiped (which
   * is every run of the dev scenarios, and any deploy where the volume moved)
   * looks like the app is broken rather than like it has no pictures.
   */
  const [broken, setBroken] = useState(false)
  useEffect(() => setBroken(false), [imageId])

  if (imageId === null || broken) {
    return (
      <span className="thumb thumb--empty" style={{ width: size, height: size }} aria-hidden="true">
        {label.trim().charAt(0).toUpperCase() || '·'}
      </span>
    )
  }
  return (
    <img
      className="thumb"
      style={{ width: size, height: size }}
      src={imageUrl(imageId)}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  )
}

export type { Attachment }
